import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { utcToZonedTime } from 'date-fns-tz';
import { getHours } from 'date-fns';
import PhoneInput, { isValidPhoneNumber, parsePhoneNumber } from 'react-phone-number-input';
import useOnClickOutside from 'use-onclickoutside';
import { get as getFromCS } from '@ts/utils/cs';
import { get as getFromLS, set as setInLS } from '@ts/utils/ls';

import selectors from '@ts/core/selectors';
import actions from '@ts/core/actions';
import apiSendInfo from '@ts/api/clickToCall';
import { ReactComponent as ArrowIcon } from '@ts/images/icons/click-to-call-arrow.svg';
import { ReactComponent as WhitePhoneIcon } from '@ts/images/icons/click-to-call-white-phone.svg';
import { ReactComponent as SuccessfulIcon } from '@ts/images/icons/click-to-call-successful.svg';
import { ReactComponent as NonWorkingHoursIcon } from '@ts/images/icons/click-to-call-non-working-hours.svg';
import { ReactComponent as CloseIcon } from '@ts/images/icons/close-icon.svg';
import { userUpdate } from '@ts/utils/iterableUtils';
import * as S from './styled';

function TalkToUs() {
    const [isOpen, setIsOpen] = useState(false);
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [questionType, setQuestionType] = useState('');
    const [highlightedQuestion, setHighlightedQuestion] = useState('Reservation');
    const [isValidPhone, setIsValidPhone] = useState(true);
    const [errorMessagePhone, setErrorMessagePhone] = useState(null);
    const [isValidEmail, setIsValidEmail] = useState(true);
    const [isValidFirstName, setIsValidFirstName] = useState(true);
    const [isValidLastName, setIsValidLastName] = useState(true);
    const [isValidQuestion, setIsValidQuestion] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [response, setResponse] = useState(null);
    const [touched, setTouched] = useState({
        talk_firstName: false,
        talk_lastName: false,
        talk_email: false,
        talk_phone: false,
        talk_question: false,
    });
    const talkToUsIsOpen = useSelector(selectors.talkToUs.makeSelectTalkToUsIsOpen());
    const dispatch = useDispatch();
    const modalRef = useRef();
    useEffect(() => {
        const dataInLS = getFromLS('_ts__contact-info');
        const dataInCS = getFromCS('user');
        setPhone(dataInLS?.user_info?.phone || '');
        setEmail(dataInLS?.user_info?.email || dataInCS?.email || '');
        setFirstName(dataInLS?.user_info?.first_name || dataInCS?.firstName || '');
        setLastName(dataInLS?.user_info?.last_name || dataInCS?.lastName || '');
    }, [talkToUsIsOpen]);
    const closeTalkToUs = () => {
        dispatch(actions.talkToUs.closeTalkToUs());
        setPhone('');
        setIsValidPhone(true);
        setEmail('');
        setIsValidEmail(true);
        setFirstName('');
        setIsValidFirstName(true);
        setLastName('');
        setIsValidLastName(true);
        setQuestionType('');
        setHighlightedQuestion('Reservation');
        setIsValidQuestion(true);
        setResponse(null);
        setIsOpen(false);
        setTouched({
            talk_firstName: false,
            talk_lastName: false,
            talk_email: false,
            talk_phone: false,
            talk_question: false,
        });
        document.querySelector('body').classList.remove('body--ovh--important');
    };
    useOnClickOutside(modalRef, closeTalkToUs);

    const onPhoneChange = useCallback(
        (value) => {
            setPhone(value || '');
        },
        [setPhone]
    );
    const onChangeEmail = useCallback(
        (evt) => {
            setEmail(evt.target.value || '');
        },
        [setEmail]
    );

    const onChangeFirstName = useCallback(
        (evt) => {
            setFirstName(evt.target.value || '');
        },
        [setFirstName]
    );

    const onChangeLastName = useCallback(
        (evt) => {
            setLastName(evt.target.value || '');
        },
        [setLastName]
    );

    const onClickQuestion = useCallback(
        (element) => {
            const questionType = element.currentTarget.dataset.type;
            setQuestionType(questionType);
            setIsOpen(false);
            setTouched({ ...touched, talk_question: true });
            setIsValidQuestion(true);
        },
        [touched, setQuestionType, setIsOpen, setTouched]
    );

    const onMouseEnterQuestion = useCallback(
        (element) => {
            const questionType = element.currentTarget.dataset.type;
            setHighlightedQuestion(questionType);
        },
        [setHighlightedQuestion]
    );

    const ZonedTime = utcToZonedTime(new Date(), 'America/Chicago');
    const isItWorkingTime = getHours(ZonedTime) >= 7 && getHours(ZonedTime) < 18;

    const formValidation = useCallback(
        (touch = touched) => {
            const letters = /^[A-Za-z]+$/;
            const parsedPhone = parsePhoneNumber(phone);
            if (!touch.talk_email || /^.+@.+\..+$/g.test(email)) {
                setIsValidEmail(true);
            } else {
                setIsValidEmail(false);
            }
            if (
                !touch.talk_phone ||
                (isValidPhoneNumber(phone) && parsedPhone && parsedPhone.country === 'US')
            ) {
                setIsValidPhone(true);
                setErrorMessagePhone(null);
            } else {
                if (!(parsedPhone && parsedPhone.country === 'US')) {
                    setErrorMessagePhone('Please enter an American phone number');
                }
                if (!isValidPhoneNumber(phone)) {
                    setErrorMessagePhone('Please enter valid phone-number');
                }
            }
            setIsValidPhone(
                isValidPhoneNumber(phone) && parsedPhone && parsedPhone.country === 'US'
            );
            if (!touch.talk_firstName || (firstName.trim().length && firstName.match(letters))) {
                setIsValidFirstName(true);
            } else {
                setIsValidFirstName(false);
            }
            if (!touch.talk_lastName || (lastName.trim().length && lastName.match(letters))) {
                setIsValidLastName(true);
            } else {
                setIsValidLastName(false);
            }
            if (touch.talk_question && questionType) {
                setIsValidQuestion(true);
            } else {
                setIsValidQuestion(false);
            }
            if (
                !/^.+@.+\..+$/g.test(email) ||
                !isValidPhoneNumber(phone) ||
                !(parsedPhone && parsedPhone.country === 'US') ||
                !(firstName.trim().length && firstName.match(letters)) ||
                !(lastName.trim().length && lastName.match(letters))
            ) {
                return false;
            }
            return true;
        },
        [
            phone,
            email,
            firstName,
            lastName,
            touched,
            questionType,
            setIsValidEmail,
            setIsValidPhone,
            setIsValidFirstName,
            setIsValidLastName,
            setIsValidQuestion,
        ]
    );

    const onClickSubmit = useCallback(() => {
        const allTouched = {
            talk_firstName: true,
            talk_lastName: true,
            talk_email: true,
            talk_phone: true,
            talk_question: true,
        };
        setTouched(allTouched);
        const isTheFormValid = formValidation(allTouched);
        if (!isTheFormValid) {
            return;
        }
        setSubmitting(true);

        apiSendInfo({
            phone,
            firstName,
            lastName,
            email,
            questionType,
            href: window.location.href,
        })
            .then((res) => setResponse(res))
            .finally(() => {
                setSubmitting(false);
            });
        userUpdate(email, {
            firstName: firstName,
            lastName: lastName,
            fullName: firstName + ' ' + lastName,
            picture: null,
            phoneNumber: phone,
        });
        setInLS('_ts__contact-info', {
            user_info: {
                phone,
                email,
                first_name: firstName,
                last_name: lastName,
            },
        });
    }, [phone, firstName, lastName, email, questionType, formValidation]);

    const validationAfterTouch = (element) => {
        const elementID = element.currentTarget.id;
        const newTouched = { ...touched, [elementID]: true };
        setTouched(newTouched);
        formValidation(newTouched);
    };
    return (
        <S.Wrapper talkToUsIsOpen={talkToUsIsOpen}>
            <S.ModalWrapper>
                <S.Modal ref={modalRef}>
                    <S.Heading>
                        <S.HeadTextWrapper>
                            <WhitePhoneIcon />
                            Talk to us
                        </S.HeadTextWrapper>
                        <CloseIcon onClick={closeTalkToUs} />
                    </S.Heading>

                    {isItWorkingTime && response && response.data.success && (
                        <S.InfoMessage>
                            <S.WrapperForIcon>
                                <SuccessfulIcon />
                            </S.WrapperForIcon>
                            <span>
                                Thanks {firstName}.
                                <br />
                                We will be calling {phone} in just a few moments.
                                <br />
                                <br />
                                Feel free to browse the site and continue shopping. Closing this
                                window will not affect your place in line.
                            </span>
                        </S.InfoMessage>
                    )}
                    {!isItWorkingTime && (
                        <S.InfoMessage>
                            <S.WrapperForIcon>
                                <NonWorkingHoursIcon />
                            </S.WrapperForIcon>
                            <span>
                                We are currently closed.
                                <br />
                                Please call us during normal business hours or book online 24/7.
                                <br />
                                <br />
                                <div>
                                    We are open 7am - 7pm <br />
                                    CST Monday-Sunday
                                </div>
                            </span>
                        </S.InfoMessage>
                    )}
                    {isItWorkingTime && (!response || !response.data.success) && (
                        <>
                            <S.SubHeading>
                                Fill out your information and a reservationist will call you right
                                back.
                            </S.SubHeading>

                            <S.FormGroup error={!isValidFirstName}>
                                <S.Label>First Name</S.Label>
                                <S.Input
                                    id="talk_firstName"
                                    placeholder="your first name"
                                    value={firstName}
                                    onChange={onChangeFirstName}
                                    onBlur={validationAfterTouch}
                                />
                                {!isValidFirstName && (
                                    <S.ErrorText>Please enter valid first name</S.ErrorText>
                                )}
                            </S.FormGroup>

                            <S.FormGroup error={!isValidLastName}>
                                <S.Label>Last Name</S.Label>
                                <S.Input
                                    id="talk_lastName"
                                    placeholder="your last name"
                                    value={lastName}
                                    onChange={onChangeLastName}
                                    onBlur={validationAfterTouch}
                                />
                                {!isValidLastName && (
                                    <S.ErrorText>Please enter valid last name</S.ErrorText>
                                )}
                            </S.FormGroup>

                            <S.FormGroup error={!isValidEmail}>
                                <S.Label>Email</S.Label>
                                <S.Input
                                    id="talk_email"
                                    placeholder="your email address"
                                    value={email}
                                    onChange={onChangeEmail}
                                    onBlur={validationAfterTouch}
                                />
                                {!isValidEmail && (
                                    <S.ErrorText>Please enter valid email</S.ErrorText>
                                )}
                            </S.FormGroup>

                            <S.FormGroup error={!isValidPhone && errorMessagePhone}>
                                <S.Label>Phone</S.Label>
                                <PhoneInput
                                    id="talk_phone"
                                    placeholder="Enter phone number"
                                    showCountrySelect={false}
                                    value={phone}
                                    onChange={onPhoneChange}
                                    onBlur={validationAfterTouch}
                                    country="US"
                                />
                                {!isValidPhone && errorMessagePhone && (
                                    <S.ErrorText>{errorMessagePhone}</S.ErrorText>
                                )}
                            </S.FormGroup>

                            <S.FormGroup>
                                <S.Label>How can we help?</S.Label>
                                <S.Control
                                    id="talk_question"
                                    onFocus={() => setTimeout(() => setIsOpen(true), 200)}
                                    onBlur={(element) => {
                                        setTimeout(() => setIsOpen(false), 200);
                                        validationAfterTouch(element);
                                    }}
                                    onClick={(element) => {
                                        validationAfterTouch(element);
                                        setIsOpen(!isOpen);
                                    }}
                                    tabIndex="0"
                                    error={!isValidQuestion && !isOpen}
                                >
                                    <S.Placeholder>
                                        {questionType === 'Reservation'
                                            ? 'I would like to make a reservation'
                                            : questionType === 'Options'
                                            ? 'I need assistance with an existing reservation'
                                            : questionType === 'ActivityQuestions'
                                            ? 'I have activity questions'
                                            : 'Choose one ...'}
                                    </S.Placeholder>
                                    <S.Indicators>
                                        <S.IndicatorContainer>
                                            <ArrowIcon />
                                        </S.IndicatorContainer>
                                    </S.Indicators>
                                </S.Control>
                                {!isValidQuestion && !isOpen && (
                                    <S.ErrorText>Please choose type of question</S.ErrorText>
                                )}
                                <S.Menu isOpen={isOpen}>
                                    <S.MenuItem
                                        data-type="Reservation"
                                        onClick={onClickQuestion}
                                        onMouseEnter={onMouseEnterQuestion}
                                        isActive={highlightedQuestion === 'Reservation'}
                                    >
                                        I would like to make a reservation
                                    </S.MenuItem>
                                    <S.MenuItem
                                        data-type="Options"
                                        onClick={onClickQuestion}
                                        onMouseEnter={onMouseEnterQuestion}
                                        isActive={highlightedQuestion === 'Options'}
                                    >
                                        I need assistance with an existing reservation
                                    </S.MenuItem>
                                    <S.MenuItem
                                        data-type="ActivityQuestions"
                                        onClick={onClickQuestion}
                                        onMouseEnter={onMouseEnterQuestion}
                                        isActive={highlightedQuestion === 'ActivityQuestions'}
                                    >
                                        I have activity questions
                                    </S.MenuItem>
                                </S.Menu>
                            </S.FormGroup>

                            {response && !response.success && (
                                <S.ErrorMessage>{response.errorMessage}</S.ErrorMessage>
                            )}

                            <S.Actions>
                                <S.SubmitButton disabled={submitting} onClick={onClickSubmit}>
                                    Call Me{submitting && '...'}
                                </S.SubmitButton>
                            </S.Actions>
                        </>
                    )}
                </S.Modal>
            </S.ModalWrapper>
        </S.Wrapper>
    );
}

export default TalkToUs;
